import React, { Suspense, useMemo } from 'react';

import { AnalyticsContext } from '@atlaskit/analytics-next';
import { fg } from '@atlaskit/platform-feature-flags';
import UFOSegment from '@atlaskit/react-ufo/segment';
import { RelayEnvironmentProvider } from '@atlassian/relay-environment-provider';

import { FeatureExposedAnalytics } from '../analytics/feature-exposed-analytics';
import {
	EditionAwarenessContext,
	type EditionAwarenessContextType,
	useEditionAwareness,
} from '../services/edition-awareness-context';
import {
	ExperimentContext,
	ExperimentContextClass,
	treatmentCohortTransformFn,
	useGetExperimentValue,
} from '../services/experiment-context';
import { getTrialOfferingName } from '../services/get-trial-offering-name';
import { isEligibleForStandardToPremium } from '../services/is-eligible-for-standard-to-premium';
import type { Edition, ExperimentCohort, Product, UserRole } from '../types';

import { StandardToPremiumModal } from './dialogs/standard-to-premium-modal';
import { ErrorBoundary } from './error-boundary';
import { Spotlights } from './spotlights';

export type EditionAwarenessUpsellProps = {
	cloudId: string;
	edition: Edition;
	hasDismissedButton?: boolean;
	navVersion?: 'nav3' | 'nav4';
	onDismissButton?: () => void;
	originalProjectTypeWasCore?: boolean;
	product: Product;
	userRoles?: UserRole[];
};

/**
 * Edition awareness upsell is an experimental EA implementtiona that has to be exported as its own component to avoid making ccp calls which are alerting VC90 mertics
 *
 * This component deals with creating the context which is then used throughout the package to:
 * - Track analytics
 * - Know which product/edition/cloud id is being used
 * - Know when to show/hide the button
 */
function EditionAwarenessUpsellImpl({
	edition,
	product,
	cloudId,
	userRoles,
	hasDismissedButton,
	onDismissButton,
	navVersion,
	originalProjectTypeWasCore,
}: EditionAwarenessUpsellProps) {
	const trialOfferingName = getTrialOfferingName(product, edition);

	const context: EditionAwarenessContextType = useMemo(() => {
		return {
			cloudId,
			product,
			edition,
			userRoles,
			hasDismissedButton,
			onDismissButton,
			trialOfferingName,
			// This is not used in this flow, but a required type in context
			licensedUsers: 1,
			// This is displayed to sites that are on not trial not predunning
			invoiceableStatus: 'INVOICEABLE',
			originalProjectTypeWasCore,
		};
	}, [
		cloudId,
		product,
		edition,
		userRoles,
		hasDismissedButton,
		onDismissButton,
		trialOfferingName,
		originalProjectTypeWasCore,
	]);

	const experimentContextValue = useMemo(() => new ExperimentContextClass(), []);

	return (
		<ExperimentContext.Provider value={experimentContextValue}>
			<EditionAwarenessContext.Provider value={context}>
				<AnalyticsContext data={{ navVersion, hasDismissedButton }}>
					{fg('remove-spotlight-edition-awareness-fg') ? (
						<StandardEdition />
					) : (
						<Spotlights>
							<StandardEdition />
						</Spotlights>
					)}
				</AnalyticsContext>
			</EditionAwarenessContext.Provider>
		</ExperimentContext.Provider>
	);
}

export function EditionAwarenessUpsell(props: EditionAwarenessUpsellProps): React.JSX.Element {
	const { product, edition } = props;

	return (
		<RelayEnvironmentProvider>
			<UFOSegment name="edition-awareness-upsell">
				{/* This needs to be something that is not null, if it is null then the error will
				bubble up to the parent as `ErrorBoundary` will throw an exception if it is passed `null` */}
				<ErrorBoundary product={product} edition={edition} id="edition-awareness-upsell">
					<Suspense fallback={null}>
						<EditionAwarenessUpsellImpl {...props} />
					</Suspense>
				</ErrorBoundary>
			</UFOSegment>
		</RelayEnvironmentProvider>
	);
}

function StandardEdition() {
	const context = useEditionAwareness();
	const { product, hasDismissedButton, originalProjectTypeWasCore } = context;
	const { getExperimentValue, getExperimentValueWithTransform } = useGetExperimentValue();

	if (isEligibleForStandardToPremium(context) && !hasDismissedButton) {
		// Firing exposure for the introduction of full JWM support
		if (
			product === 'jira' &&
			originalProjectTypeWasCore &&
			getExperimentValue({
				statsigParams: {
					experimentName: 'twcg-1563-remove-jira-only-restriction',
					parameterName: 'cohort',
					defaultValue: 'not-enrolled',
				},
			}) !== 'variation'
		) {
			// If the tenant is not in variation, we don't show anything, as that is the original JWM experience
			return null;
		}

		let experimentKey: string;
		if (product === 'confluence') {
			experimentKey = 'edition_awareness_standard_upsell_confluence_ab';
		} else if (product === 'jira-product-discovery') {
			experimentKey = 'edition_awareness_upsell_jpd';
		} else {
			experimentKey = 'edition_awareness_upsell_jira_ab';
		}

		const cohort = getExperimentValueWithTransform<ExperimentCohort>({
			statsigParams: {
				experimentName: experimentKey,
				parameterName: 'cohort',
				defaultValue: 'not-enrolled',
			},
			transformCohortFunc: treatmentCohortTransformFn,
		});

		return (
			<>
				{cohort === 'control' && (
					<FeatureExposedAnalytics flagKey={experimentKey} cohort={cohort} />
				)}
				{cohort === 'treatment' && (
					<FeatureExposedAnalytics flagKey={experimentKey} cohort={cohort}>
						<StandardToPremiumModal />
					</FeatureExposedAnalytics>
				)}
			</>
		);
	}

	return null;
}
